import { Tag } from 'antd';
import styled from 'styled-components';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { ColumnFormat, type IColumn, type IComponentSchema } from '../types';
import { dateFormat, DateFormat } from '../../utils/formatDate';
import { parseRuntimeTemplateString } from './data-parser';
import ComponentRenderer from '../ComponentRenderer';
import { parseJSONSchema } from './data-parser';
import { stripDollarVariables } from './formFormatters';

const SuccessIcon = styled(CheckCircleOutlined)`
  color: ${({ theme }) => theme.colors.success};
  font-size: 16px;
`;

const ErrorIcon = styled(CloseCircleOutlined)`
  color: ${({ theme }) => theme.colors.error};
  font-size: 16px;
`;

// TODO: remove this if we don't need it
// const parseValue = (value: any, data: Record<string, any>): any => {
//   if (isString(value)) {
//     return parseRuntimeTemplateString(value, data, {});
//   }

//   if (isArray(value)) {
//     return value.map(item => parseValue(item, data));
//   }

//   if (isPlainObject(value)) {
//     return parseObjectTemplates(value, data);
//   }

//   return value;
// };

// const parseObjectTemplates = (
//   obj: Record<string, any>,
//   data: Record<string, any>
// ): Record<string, any> => {
//   return mapValues(obj, (value: any) => parseValue(value, data));
// };

// const parseSchemaForRowContext = (
//   schema: IComponentSchema<any>,
//   record: any
// ): IComponentSchema<any> => {
//   const result = cloneDeep(schema);
//   const contextData = { row: record };

//   return parseJSONSchema(result, contextData);

//   if (isPlainObject(result['x-component-props'])) {
//     result['x-component-props'] = parseObjectTemplates(
//       result['x-component-props'],
//       contextData
//     );
//   }

//   if (isPlainObject(result['x-decorator-props'])) {
//     result['x-decorator-props'] = parseObjectTemplates(
//       result['x-decorator-props'],
//       contextData
//     );
//   }

//   if (isPlainObject(result.properties)) {
//     result.properties = mapValues(result.properties, property =>
//       isPlainObject(property)
//         ? parseSchemaForRowContext(property as IComponentSchema<any>, record)
//         : property
//     );
//   }

//   return result as IComponentSchema<any>;
// };

export const createColumnRenderer = (format: ColumnFormat) => {
  switch (format) {
    case ColumnFormat.BOOLEAN:
      return (value: any) => {
        const boolValue = Boolean(value);
        return boolValue ? <SuccessIcon /> : <ErrorIcon />;
      };
    case ColumnFormat.TAG:
      return (value: any) => {
        if (!value) return null;
        const stringValue = String(value);
        return <Tag>{stringValue}</Tag>;
      };
    case ColumnFormat.DATE:
      return (value: any) => {
        if (!value) return null;
        return dateFormat(value, DateFormat.Date);
      };
    case ColumnFormat.DATETIME:
      return (value: any) => {
        if (!value) return null;
        return dateFormat(value, DateFormat.DateAndTime);
      };
    case ColumnFormat.TIME:
      return (value: any) => {
        if (!value) return null;
        return dateFormat(value, DateFormat.Time);
      };
    case ColumnFormat.REFERENCE:
      return (value: any) => {
        if (!value) return null;
        if (Array.isArray(value)) {
          const displayValues = value
            .map((v: any) => v?._formDisplayValue)
            .filter(Boolean);
          return displayValues.length > 0 ? displayValues.join(', ') : null;
        }

        if (typeof value === 'object' && '_formDisplayValue' in value) {
          return value._formDisplayValue;
        }

        return null;
      };
    case ColumnFormat.CUSTOM:
    case ColumnFormat.STRING:
    default:
      return undefined;
  }
};

const renderCustomColumnSchema = ({
  schema,
  record,
  context,
  rowName = 'row',
}: {
  schema: IComponentSchema<any>;
  record: any;
  context: any;
  rowName?: string;
}): React.ReactNode => {
  const contextData = { ...context, [rowName]: record };
  const parsedSchema = parseJSONSchema(schema, contextData);

  return (
    <ComponentRenderer schema={parsedSchema} parentContext={contextData} />
  );
};

const getNestedValue = (record: any, path: string | string[]): any => {
  if (Array.isArray(path)) {
    return path.reduce((obj, key) => obj?.[key], record);
  }
  return record[path];
};

/**
 * Creates a column renderer that extracts values from records and applies formatting.
 * If col.value is set, it parses template strings using {%...%} syntax (Formily-safe).
 * Example: {%@.name%} or {%@.firstName%} {%@.lastName%}
 * If formatter is provided, it applies the format rendering to the extracted value.
 * For CUSTOM format columns, renders the customComponentSchema using ComponentRenderer.
 */
export const createColumnValueRenderer = ({
  column,
  formatter,
  context,
  rowName = 'row',
}: {
  column: IColumn;
  formatter?: (value: any) => React.ReactNode;
  context: any;
  rowName?: string;
}) => {
  return (_: any, record: any) => {
    if (column.format === ColumnFormat.CUSTOM && column.customComponentSchema) {
      return renderCustomColumnSchema({
        schema: column.customComponentSchema,
        record,
        context,
        rowName,
      });
    }
    let rawValue = column.value
      ? parseRuntimeTemplateString(
          stripDollarVariables(column.value.trim()),
          { [rowName]: record },
          {}
        )
      : getNestedValue(record, column.dataIndex);

    if (
      (rawValue === undefined || rawValue === null || rawValue === '') &&
      column.defaultValue
    ) {
      rawValue = column.defaultValue;
    }

    return formatter ? formatter(rawValue) : rawValue;
  };
};
